import { toast } from "$global-providers/toast-provider";
import { Box, Button, Collapse, styled, Typography } from "@material-ui/core";
import { FileCopyOutlined } from "@material-ui/icons";
import { EpocusProgramIngressChannel } from "elephant-web/gql";
import { useState } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import type { EpocusProgramIngressQuery } from "./__generated__/epocus-settings-operations.gql";
import { SettingsCard, SettingsCardLabel } from "./settings-card";

type Endpoint =
  EpocusProgramIngressQuery["epocusProgramIngress"]["endpoints"][number];

const STORAGE_KEY = "epocus.connectADevice.openChannel";

const CHANNEL_LABELS: Record<EpocusProgramIngressChannel, string> = {
  [EpocusProgramIngressChannel.Dicomweb]: "DICOMweb",
  [EpocusProgramIngressChannel.Dimse]: "DIMSE",
  [EpocusProgramIngressChannel.Mwl]: "Worklist (MWL)",
};

const readOpenChannel = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeOpenChannel = (channel: string | null): void => {
  try {
    if (channel === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, channel);
    }
  } catch {
    // Storage blocked — the section still opens, it just reopens collapsed.
  }
};

const MonoValue = styled(Typography)({
  fontSize: rem(14),
  fontWeight: 700,
  fontFamily: "monospace",
  color: EPOCUS_TEXT_PRIMARY,
  wordBreak: "break-all",
});

const ValueRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
});

const FieldLabel = styled(Typography)({
  fontSize: rem(11),
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
});

const Hint = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

const SectionRow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 10,
  borderTop: `1px solid ${EPOCUS_BORDER}`,
  paddingTop: 12,
});

const SummaryRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  cursor: "pointer",
});

const SectionBody = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  paddingTop: 12,
});

const copyToClipboard = async (label: string, value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error(`Couldn't copy the ${label.toLowerCase()}`);
  }
};

const CopyableField: FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    <ValueRow>
      <MonoValue>{value}</MonoValue>
      <Button
        size="small"
        variant="outlined"
        startIcon={<FileCopyOutlined />}
        onClick={() => copyToClipboard(label, value)}
        style={{ textTransform: "none", flexShrink: 0 }}
        aria-label={`Copy ${label.toLowerCase()}`}
      >
        Copy
      </Button>
    </ValueRow>
  </Box>
);

const PlainField: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    <MonoValue>{value}</MonoValue>
  </Box>
);

const DicomwebFields: FC<{ endpoint: Endpoint }> = ({ endpoint }) => (
  <>
    {endpoint.exportUrl && (
      <CopyableField label="Export URL" value={endpoint.exportUrl} />
    )}
    {endpoint.username && (
      <CopyableField label="Username" value={endpoint.username} />
    )}
    <Hint>
      {endpoint.username
        ? "Password: ask your program admin."
        : "Username and password: ask your program admin."}
    </Hint>
    <Hint>
      The URL carries the program, so the device needs no AE title or station
      settings.
    </Hint>
  </>
);

const DimseFields: FC<{ endpoint: Endpoint }> = ({ endpoint }) => (
  <>
    {endpoint.host && <CopyableField label="Host" value={endpoint.host} />}
    {endpoint.port !== null && endpoint.port !== undefined && (
      <PlainField label="Port" value={String(endpoint.port)} />
    )}
    {endpoint.calledAeTitle && (
      <CopyableField label="Called AE title" value={endpoint.calledAeTitle} />
    )}
    {endpoint.callingAeTitleRules.map((rule) => (
      <Hint key={rule.vendor}>
        {rule.vendor}: set the device's own AE title to {rule.callingAeTitle} —
        it is fixed in firmware and must match.
      </Hint>
    ))}
  </>
);

const MwlFields: FC<{ endpoint: Endpoint }> = ({ endpoint }) => (
  <>
    {endpoint.host && <CopyableField label="Host" value={endpoint.host} />}
    {endpoint.port !== null && endpoint.port !== undefined && (
      <PlainField label="Port" value={String(endpoint.port)} />
    )}
    <Hint>
      A device identifies itself to the worklist by its own AE title. Each
      performer registers that title under Probes in their Settings, then sets
      it on the device as the device's own AE title and, where the device asks
      for one separately, as the scheduled station AE title.
    </Hint>
    <Hint>
      The program AE title is the called AE title for storing scans, not the
      device's own. A device set up with the program AE title as its own still
      receives the worklist, but shares its identity with every other device set
      up that way.
    </Hint>
  </>
);

const FIELDS_BY_CHANNEL: Record<
  EpocusProgramIngressChannel,
  FC<{ endpoint: Endpoint }>
> = {
  [EpocusProgramIngressChannel.Dicomweb]: DicomwebFields,
  [EpocusProgramIngressChannel.Dimse]: DimseFields,
  [EpocusProgramIngressChannel.Mwl]: MwlFields,
};

const ChannelSection: FC<{
  endpoint: Endpoint;
  open: boolean;
  onToggle: () => void;
}> = ({ endpoint, open, onToggle }) => {
  const label = CHANNEL_LABELS[endpoint.channel];
  const Fields = FIELDS_BY_CHANNEL[endpoint.channel];

  return (
    <SectionRow>
      <SummaryRow
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <Box>
          <Typography
            style={{
              fontSize: rem(14),
              fontWeight: 600,
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            For {label}
          </Typography>
          {endpoint.examples.length > 0 && (
            <Hint>e.g. {endpoint.examples.join(", ")}</Hint>
          )}
        </Box>
        <Typography
          style={{
            fontSize: rem(18),
            color: EPOCUS_TEXT_SECONDARY,
            lineHeight: 1,
          }}
        >
          {open ? "∧" : "›"}
        </Typography>
      </SummaryRow>
      <Collapse in={open}>
        <SectionBody>
          <Fields endpoint={endpoint} />
        </SectionBody>
      </Collapse>
    </SectionRow>
  );
};

export const ConnectADeviceCard: FC<{
  aeTitle: string | null;
  endpoints: readonly Endpoint[];
}> = ({ aeTitle, endpoints }) => {
  const [openChannel, setOpenChannel] = useState<string | null>(
    readOpenChannel,
  );
  const enabled = endpoints.filter((endpoint) => endpoint.enabled);

  const toggle = (channel: string) => {
    const next = openChannel === channel ? null : channel;
    setOpenChannel(next);
    writeOpenChannel(next);
  };

  return (
    <SettingsCard>
      <SettingsCardLabel>Connect a device</SettingsCardLabel>

      {aeTitle && <CopyableField label="Program AE title" value={aeTitle} />}

      {enabled.length > 0 ? (
        enabled.map((endpoint) => (
          <ChannelSection
            key={endpoint.channel}
            endpoint={endpoint}
            open={openChannel === endpoint.channel}
            onToggle={() => toggle(endpoint.channel)}
          />
        ))
      ) : (
        <>
          <Hint>This deployment has no device ingest configured yet.</Hint>
          {endpoints.map(
            (endpoint) =>
              endpoint.disabledReason && (
                <Hint key={endpoint.channel}>
                  {CHANNEL_LABELS[endpoint.channel]}: {endpoint.disabledReason}
                </Hint>
              ),
          )}
        </>
      )}
    </SettingsCard>
  );
};
